import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import BrandMark from "@/components/BrandMark";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/#about" },
  { label: "In-Home Care", to: "/senior-care" },
  { label: "Relocation", to: "/relocation" },
  { label: "Blog", to: "/#blog" },
  { label: "FAQ", to: "/#faq" },
  { label: "Consultation", to: "/#contact" },
];

const testId = (label) => label.replace(/\s+/g, "-").toLowerCase();

const Brand = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
    <BrandMark variant="compact" />
    <span className="flex flex-col leading-none">
      <span className="font-serif text-xl text-[var(--gg-deep)] tracking-tight font-semibold">
        Graceful Guidance
      </span>
      <span className="text-[10px] tracking-[0.28em] uppercase text-[var(--gg-text-muted)] mt-1">
        Ltd. · Jamaica
      </span>
    </span>
  </Link>
);

const DesktopNav = ({ onNavClick }) => (
  <>
    <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
      {NAV.map((n) => (
        <Link
          key={n.label}
          to={n.to}
          onClick={onNavClick(n.to)}
          data-testid={`nav-${testId(n.label)}`}
          className="text-sm text-[var(--gg-text)] hover:text-[var(--gg-primary)] transition-colors font-medium"
        >
          {n.label}
        </Link>
      ))}
    </nav>
    <div className="hidden lg:flex items-center gap-3">
      <Link
        to="/#contact"
        onClick={onNavClick("/#contact")}
        data-testid="header-cta"
        className="inline-flex items-center justify-center bg-[var(--gg-primary)] text-white hover:bg-[var(--gg-primary-dark)] transition-colors rounded-full px-6 py-3 text-sm tracking-wide shadow-md shadow-[var(--gg-primary)]/25"
      >
        Book Consultation
      </Link>
    </div>
  </>
);

const MobileNav = ({ onNavClick }) => (
  <div
    className="lg:hidden pb-6 pt-2 space-y-1 animate-fade-up bg-white/98 rounded-b-xl -mx-6 px-6"
    data-testid="mobile-nav"
  >
    {NAV.map((n) => (
      <Link
        key={n.label}
        to={n.to}
        onClick={onNavClick(n.to)}
        data-testid={`mobile-nav-${testId(n.label)}`}
        className="block text-base py-3 text-[var(--gg-deep)] font-medium border-b border-[var(--gg-border)]"
      >
        {n.label}
      </Link>
    ))}
    <Link
      to="/#contact"
      onClick={onNavClick("/#contact")}
      data-testid="mobile-header-cta"
      className="inline-flex items-center bg-[var(--gg-primary)] text-white rounded-full px-6 py-3 text-sm mt-4 font-medium"
    >
      Book Consultation
    </Link>
  </div>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const handleNavClick = (to) => (e) => {
    if (to.includes("#")) {
      const [path, hash] = to.split("#");
      const targetPath = path || "/";
      e.preventDefault();
      if (location.pathname !== targetPath) {
        navigate(targetPath);
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }
    }
    setOpen(false);
  };

  const solidBg = scrolled || open;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solidBg
          ? "backdrop-blur-xl bg-white/95 border-b border-[var(--gg-border)] shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          <Brand />
          <DesktopNav onNavClick={handleNavClick} />
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-[var(--gg-deep)] bg-white/70 rounded-full backdrop-blur"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && <MobileNav onNavClick={handleNavClick} />}
      </div>
    </header>
  );
}
